import React, { useEffect, useMemo, useState } from "react";
import "./ClientPortfolioOverview.css";
import EmailIcon from "@mui/icons-material/Email";
import CallIcon from "@mui/icons-material/Call";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import EventIcon from "@mui/icons-material/Event";
import axiosInstance from "../../../Config/axiosInstance";
import { BarChart, PolarisVizProvider } from "@shopify/polaris-viz";
import "@shopify/polaris-viz/build/esm/styles.css";

function HealthRiskChart( clientData ) {
  const id = clientData.clientData.id
  const BAR_GROUP_WIDTH = 150;
  const [health, setHealth] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosInstance.get("/anaf-query/health-risk/", {
          params: { client: id }
        });

        setHealth(response.data)
      } catch (error) {
        console.log(error)
      } finally {
        setLoading(false);
      }
    }
    
    fetchData()
  }, [id])

  let data = []

  if (health === null) {
    data = []
  }
  else {
    data = [
      {
        name: "Relationship",
        color: "#74C0FC",
        data: health.map((entry) => ({ key: String(entry.year), value: entry.timeline_score })),
      },
      {
        name: "Anaf financiar",
        color: "#9775FA",
        data: health.map((entry) => ({ key: String(entry.year), value: entry.bilant_score })),
      },
    ];
  }

  return (
    <div style={{ width: '100%', height: 'auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column'}}>
        
        <div style={{ display: 'flex', flexDirection: 'row'}}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, justifyContent: 'space-between', paddingBottom: 36 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#FA5252", display: "inline-block" }} />
              Critical
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#FD7E14", display: "inline-block" }} />
              Mediu
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#40C057", display: "inline-block" }} />
              Low
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#85B7EB", display: "inline-block" }} />
              Unknown
            </div>
          </div>

          <PolarisVizProvider>
            <div className="client-portfolio-overview-chart-scroll-container" style={{ width: '100%', overflowX: 'auto', paddingTop: 4, scrollbarWidth: 'thin', paddingBottom: 12, marginLeft: 12 }}>
              {loading ? (
                <div style={{ textAlign: "center", color: "#aaa", fontSize: 13, padding: "32px 0" }}>
                  Loading chart...
                </div>
              ) : health?.length > 0 ? (
              <div style={{ width: health.length * BAR_GROUP_WIDTH }}>
                <BarChart
                  data={data}
                  type="grouped"
                  isAnimated
                  showLegend={false}
                  xAxisOptions={{ hide: false }}
                  yAxisOptions={{
                    max: 3,
                    placement: "left",
                    labelFormatter: (v) => {
                      if (v >= 2) return "";
                      if (v >= 1) return "";
                      return "";
                    },
                  }}
                />
              </div>
              ) : (
                <div style={{ textAlign: "center", color: "#aaa", fontSize: 13, padding: "32px 0" }}>
                  No data available
                </div>
              )}
            </div>
          </PolarisVizProvider>
        </div>

        <div style={{ display: "flex", gap: 16, width: '100%', justifyContent: 'center', marginTop: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#444" }}>
            <div style={{ width: 12, height: 12, borderRadius: 2, background: "#74C0FC" }} />
            Relationship
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#444" }}>
            <div style={{ width: 12, height: 12, borderRadius: 2, background: "#9775FA" }} />
            Anaf financiar
          </div>
        </div>

      </div>
    </div>
  );
}

function ClientPortfolioOverview({ clientData }) {
  const [timelineData, setTimelineData] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(true);
  const [timelineError, setTimelineError] = useState("");

  const [activeProjects, setActiveProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [projectsError, setProjectsError] = useState("");

  useEffect(() => {
    const fetchTimeline = async () => {
      if (!clientData?.id) {
        setTimelineData([]);
        setLoadingTimeline(false);
        return;
      }

      try {
        setLoadingTimeline(true);
        setTimelineError("");

        const response = await axiosInstance.get(
          `/timeline/?client=${clientData.id}`
        );

        setTimelineData(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        setTimelineData([]);
        setTimelineError("Could not load timeline data.");
      } finally {
        setLoadingTimeline(false);
      }
    };

    fetchTimeline();
  }, [clientData]);

  useEffect(() => {
    const fetchActiveProjects = async () => {
      if (!clientData?.id) {
        setActiveProjects([]);
        setLoadingProjects(false);
        return;
      }

      try {
        setLoadingProjects(true);
        setProjectsError("");

        const response = await axiosInstance.get(
          `/code_registries/?status=Active&client=${clientData.id}`
        );

        const projects = Array.isArray(response.data) ? response.data : [];

        setActiveProjects(projects);
      } catch (error) {
        setActiveProjects([]);
        setProjectsError("Could not load active projects.");
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchActiveProjects();
  }, [clientData]);

  const normalizeDate = (dateString) => {
    const date = new Date(dateString);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";

    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "Email":
        return <EmailIcon sx={{ fontSize: "16px" }} />;
      case "Call":
        return <CallIcon sx={{ fontSize: "16px" }} />;
      case "Meeting":
        return <MeetingRoomIcon sx={{ fontSize: "16px" }} />;
      default:
        return <EventIcon sx={{ fontSize: "16px" }} />;
    }
  };

  const { latestPastItem, nextFollowUpItem, latestInteractions } = useMemo(() => {
    const today = new Date();
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const pastOrToday = [...timelineData]
      .filter((item) => normalizeDate(item.event_date) <= todayOnly)
      .sort((a, b) => new Date(b.event_date) - new Date(a.event_date));

    const future = [...timelineData]
      .filter((item) => normalizeDate(item.event_date) > todayOnly)
      .sort((a, b) => new Date(a.event_date) - new Date(b.event_date));

    const latestPast = pastOrToday.length ? pastOrToday[0] : null;

    const allRelevant = [ ...(latestPast ? [latestPast] : []), ...future ].sort(
      (a, b) => new Date(b.event_date) - new Date(a.event_date)
    );

    return {
      latestPastItem: latestPast,
      nextFollowUpItem: future.length ? future[0] : null,
      latestInteractions: allRelevant,
    };
  }, [timelineData]);

  return (
    <div className="client-portfolio-overview">

      <div className="overview-card" style={{ overflowX: "auto", marginBottom: 16 }}>
        <div className="overview-card-header">
          <div>
            <h2>Health Risk</h2>
          </div>
        </div>
        <HealthRiskChart clientData={clientData}/>
      </div>

      <div className="client-portfolio-overview-grid">
        <div className="overview-card">
          <div className="overview-card-header">
            <div>
              <h2>Now / Next</h2>
              <p>Upcoming actions and follow-ups</p>
            </div>
          </div>

          <div className="overview-summary-grid">
            <div className="overview-summary-box">
              <span className="overview-summary-label">Last interaction</span>
              <strong className="overview-summary-value">
                {latestPastItem ? formatDate(latestPastItem.event_date) : "-"}
              </strong>
            </div>

            <div className="overview-summary-box">
              <span className="overview-summary-label">Upcoming follow-up</span>
              <strong className="overview-summary-value">
                {nextFollowUpItem ? formatDate(nextFollowUpItem.event_date) : "-"}
              </strong>
            </div>
          </div>

          <div className="overview-section">
            <h3>Recent & Upcoming Interactions</h3>

            {loadingTimeline ? (
              <div className="overview-empty-state">Loading timeline...</div>
            ) : timelineError ? (
              <div className="overview-error-state">{timelineError}</div>
            ) : latestInteractions.length ? (
              <div className="overview-timeline">
                {latestInteractions.map((item, index) => {
                  const isLast = index === latestInteractions.length - 1;
                  const colorClass = isLast ? "gray" : "blue";

                  return (
                    <div key={item.id} className="overview-timeline-item">
                      <div className="overview-timeline-left">
                        <span
                          className={`overview-timeline-dot ${colorClass}`}
                        ></span>

                        {!isLast && (
                          <span
                            className={`overview-timeline-line ${colorClass}`}
                          ></span>
                        )}
                      </div>

                      <div className="overview-timeline-content">
                        <div className="overview-timeline-top">
                          <span className="overview-timeline-type-icon">
                            {getTypeIcon(item.type)}
                          </span>
                          <span className="overview-timeline-title">
                            {item.type}: {formatDate(item.event_date)}
                          </span>
                        </div>

                        <div className="overview-timeline-description">
                          {item.description || "-"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="overview-empty-state">
                There are no timeline records.
              </div>
            )}
          </div>
        </div>

        <div className="overview-card">
          <div className="overview-card-header overview-card-header-simple">
            <div>
              <h2>Works in Progress</h2>
              <p>Projects</p>
            </div>
          </div>

          <div className="overview-projects-wrapper">
            <div className="overview-projects-header">
              <h3>Active Projects</h3>
            </div>

            {loadingProjects ? (
              <div className="overview-empty-state">Loading active projects...</div>
            ) : projectsError ? (
              <div className="overview-error-state">{projectsError}</div>
            ) : activeProjects.length ? (
              <div className="overview-projects-list">
                {activeProjects.map((project) => (
                  <div
                    key={project.id}
                    className="overview-project-card"
                  >
                    <div className="overview-project-row">
                      <span className="overview-project-name">
                        {project.code || "-"}
                      </span>
                    </div>

                    <div className="overview-project-row">
                      <span className="overview-project-description">
                        {project.description || "-"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="overview-empty-state">
                There are no active projects for this client.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClientPortfolioOverview;